import { useState, useEffect, useCallback, useRef } from "react";
import { GfImage } from "@swagger/typescript-fetch-goflyer";
import { LoadedImageData } from "../types";
import { IMAGE_LOADER_CONSTANTS } from "../constants";

interface UseImageLoaderProps {
  gfImages: GfImage[];
  containerWidth: number;
  containerHeight: number;
}

interface UseImageLoaderReturn {
  loadedImages: Map<number, LoadedImageData>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook for loading and scaling flyer images to fit within a container.
 *
 * This hook handles the asynchronous loading of multiple flyer images, automatically
 * scaling them to fit within the specified container dimensions while maintaining aspect
 * ratio. Images are sorted by flyerIndex and validated before loading. The hook prevents
 * race conditions when images change by tracking loading operations and only applying
 * results from the most recent request.
 *
 * Key features:
 * - Loads multiple images in parallel
 * - Scales images to fit container with padding
 * - Prevents race conditions with loading ID tracking
 * - Cleans up image resources on unmount
 * - Provides loading and error states
 *
 * @param props - Hook configuration
 * @param props.gfImages - Array of image objects to load
 * @param props.containerWidth - Width of the container in pixels
 * @param props.containerHeight - Height of the container in pixels
 * @returns Object containing loaded images map, loading state, and error state
 */
export const useImageLoader = ({
  gfImages,
  containerWidth,
  containerHeight,
}: UseImageLoaderProps): UseImageLoaderReturn => {
  const [loadedImages, setLoadedImages] = useState<
    Map<number, LoadedImageData>
  >(new Map());
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadingRef = useRef<number>(0);

  /**
   * Loads all flyer images asynchronously with scaling and validation.
   *
   * Filters out invalid images (missing flyerIndex or src), sorts by flyerIndex,
   * and loads all images in parallel. Each image is scaled to fit within the
   * container while maintaining aspect ratio with padding. Uses loading IDs to
   * prevent race conditions when images change during loading.
   */
  const loadImages = useCallback(async () => {
    const currentLoadId = ++loadingRef.current;

    if (!gfImages || gfImages.length === 0) {
      console.warn("[useImageLoader] No flyer images available");
      setError("No flyer images available");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const sortedImages = [...gfImages]
      .filter((img) => img.flyerIndex !== undefined && img.src)
      .sort((a, b) => (a.flyerIndex || 0) - (b.flyerIndex || 0));

    if (sortedImages.length === 0) {
      setError("No valid flyer images found (missing flyerIndex or src)");
      setIsLoading(false);
      return;
    }

    const imagePromises = sortedImages.map((gfImage, index) => {
      return new Promise<LoadedImageData>((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          const naturalWidth = img.naturalWidth;
          const naturalHeight = img.naturalHeight;

          const padding = IMAGE_LOADER_CONSTANTS.PADDING_RATIO;
          const scaleX = (containerWidth * padding) / naturalWidth;
          const scaleY = (containerHeight * padding) / naturalHeight;
          const scale = Math.min(scaleX, scaleY, 1);

          const scaledWidth = naturalWidth * scale;
          const scaledHeight = naturalHeight * scale;

          resolve({
            image: img,
            width: scaledWidth,
            height: scaledHeight,
            naturalWidth,
            naturalHeight,
            scaleX: scale,
            scaleY: scale,
            flyerIndex: gfImage.flyerIndex || 0,
          });
        };

        img.onerror = (error) => {
          console.error(
            `useImageLoader: Failed to load image ${index + 1}/${sortedImages.length}`,
            {
              flyerIndex: gfImage.flyerIndex,
              src: gfImage.src,
              error,
            },
          );
          reject(new Error(`Failed to load image: ${gfImage.src}`));
        };

        img.src = gfImage.src;
      });
    });

    try {
      const loadedImageDataArray = await Promise.all(imagePromises);

      if (currentLoadId !== loadingRef.current) {
        return;
      }

      const imageMap = new Map<number, LoadedImageData>();

      // Store images using sequential page numbers (1, 2, 3...) based on array position
      // This ensures consistent retrieval regardless of the actual flyerIndex values
      loadedImageDataArray.forEach((imageData, index) => {
        const pageNumber = index + 1; // Sequential: 1, 2, 3...
        imageMap.set(pageNumber, imageData);
      });

      setLoadedImages(imageMap);
      setIsLoading(false);
    } catch (err) {
      if (currentLoadId !== loadingRef.current) {
        return;
      }
      console.error("useImageLoader: Error loading images:", err);
      setError(err instanceof Error ? err.message : "Failed to load images");
      setIsLoading(false);
    }
  }, [gfImages, containerWidth, containerHeight]);

  useEffect(() => {
    loadImages();

    return () => {
      loadedImages.forEach((imageData) => {
        if (imageData.image) {
          imageData.image.onload = null;
          imageData.image.onerror = null;
          imageData.image.src = "";
        }
      });
    };
  }, [loadImages]);

  return {
    loadedImages,
    isLoading,
    error,
  };
};
